import React, { memo, useCallback } from 'react'

import {
  func, number, string, oneOfType, bool, shape,
} from 'prop-types'

import CreatorAvatar from 'Pods/Shared/CreatorAvatar/CreatorAvatar'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'

// The remix button holds only the image, keeping one clean focusable control
// with no nested interactives for keyboard and screen-reader users. The creator
// avatar (which carries hover-tooltip DOM) renders as a sibling overlay
// positioned over the card, never inside the button.
function Card({
  id,
  slug = '',
  title,
  prompt,
  createdAt,
  image,
  user = null,
  onRemix,
  eager = false,
}) {
  const t = useTranslation()

  const handleRemix = useCallback(() => {
    // Pass the full surface area the downstream optimistic library entry and
    // viewer placeholder need; Card stays the only place that knows the field
    // names the BE returns. `slug` drives the stable detail URL.
    onRemix({ id, slug, title, prompt, createdAt, image })
  }, [onRemix, id, slug, title, prompt, createdAt, image])

  // The username is a hover-only affordance (see the tooltip below), so name the
  // creator in the card's accessible label to keep it reachable without hover.
  const remixLabel = user?.username
    ? t('community.card.remixBy', { title, username: user.username })
    : t('community.card.remix', { title })

  return (
    <div className="community-gallery-card">
      <button
        type="button"
        className="community-gallery-card__remix"
        onClick={handleRemix}
        aria-label={remixLabel}
      >
        <img
          className="community-gallery-card__image"
          src={image}
          alt=""
          aria-hidden="true"
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          fetchpriority={eager ? 'high' : 'low'}
        />
      </button>

      <CreatorAvatar
        user={user}
        tooltipContent={user?.username}
        id={`community-gallery-card-author-${id}`}
        place="right"
        containerClass="community-gallery-card__author"
        className="community-gallery-card__avatar"
      />

      {/* Hidden for now until we implement liking/commenting in models */}
      {/* <span className="community-gallery-card__stats">
        <span className="community-gallery-card__stat">
          <Icon svgComponent={Chat2} size={iconSizeMedium} color="dark" notSpaced />
          <span className="community-gallery-card__stat-count cgt-body-base-semibold">{commentsCount}</span>
        </span>
        <span className="community-gallery-card__stat">
          <Icon svgComponent={Like2} size={iconSizeMedium} color="dark" notSpaced />
          <span className="community-gallery-card__stat-count cgt-body-base-semibold">{likeCount}</span>
        </span>
      </span> */}
    </div>
  )
}

Card.propTypes = {
  id: oneOfType([number, string]).isRequired,
  slug: string,
  title: string.isRequired,
  prompt: string.isRequired,
  createdAt: string.isRequired,
  image: string.isRequired,
  user: shape({ username: string, avatar_url: string }),
  onRemix: func.isRequired,
  eager: bool,
}

export default memo(Card)
